import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import "../css/AminiiProductDetails.css";
import { SkeletonDetail, SkeletonCard } from "../components/Skeleton";
import AminiiErrorPage from "./AminiiErrorPage";
import AminiiNavbar from "../components/AminiiNavbar";
import AminiiFooter from "../components/AminiiFooter";
import FreeShipping from "../components/FreeShipping";
import LikeButton from "../components/LikeButton";
import { getCookie, decodeJwtPayload, isTokenExpired, refreshAccessToken } from "../Utils/authHelpers";
import { API_BASE_URL } from "../config/constants";
import { getOptimizedImageUrl, getOptimizedVideoUrl } from "../Utils/upload";
import {
  addGuestCartItem,
  getGuestCartCount,
} from "../Utils/guestCart";

const isVideoMedia = (url) => {
  if (!url || typeof url !== "string") return false;
  return (
    /\.(mp4|webm|ogg|mov|m4v)(\?.*)?$/i.test(url) ||
    url.includes("/video/upload/") ||
    url.startsWith("data:video/")
  );
};

// Inner component so useEffect fires per-slide whenever isCurrent changes.
// React's `autoPlay` prop is only applied at mount time; browsers ignore
// subsequent attribute changes. Calling .play() imperatively is the only
// reliable cross-browser (and mobile Safari) solution.
function VideoSlide({ src, isCurrent, className, extraClassName }) {
  const videoRef = useRef(null);
  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    if (isCurrent) {
      const p = el.play();
      if (p && p.catch) p.catch(() => {}); // suppress NotAllowedError if browser still blocks
    } else {
      el.pause();
    }
  }, [isCurrent]);
  return (
    <video
      ref={videoRef}
      src={src}
      className={`${className || ""} ${extraClassName || ""}`.trim()}
      playsInline
      muted
      loop
      preload="auto"
    />
  );
}

function AminiiProductDetails() {
  const { productId } = useParams();
  const [getRes, setRes] = useState(null);
  const [selectedSize, setSelectedSize] = useState(null);
  const [categoryProdu, setCategoryProdu] = useState([]);
  const [isProductLiked, setIsProductLiked] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userFirstName, setUserFirstName] = useState("");
  const [userId, setUserId] = useState(null);
  const [isMobileView, setIsMobileView] = useState(false);
  const [cartItemCount, setCartItemCount] = useState(0);
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState("message");
  const [modalMessage, setModalMessage] = useState("");
  const [startIndex, setStartIndex] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [sliderTouchStartX, setSliderTouchStartX] = useState(0);
  const [sliderTouchDeltaX, setSliderTouchDeltaX] = useState(0);
  const [isSliderSwiping, setIsSliderSwiping] = useState(false);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [isDescriptionOpen, setIsDescriptionOpen] = useState(true);
  const [showStickyMobileBar, setShowStickyMobileBar] = useState(false);
  const [isSizeGuideOpen, setIsSizeGuideOpen] = useState(false);
  const sliderRef = useRef(null);
  const carouselRef = useRef(null);

  const navigate = useNavigate();

  // Compute ordered mediaList: Always position video at index 1 (second slide/panel)
  const mediaList = React.useMemo(() => {
    if (!getRes) return [];
    const rawMediaList = Array.isArray(getRes.productImages) ? [...getRes.productImages] : [];
    if (getRes.productVideo && !rawMediaList.includes(getRes.productVideo)) {
      rawMediaList.push(getRes.productVideo);
    } else if (getRes.videoUrl && !rawMediaList.includes(getRes.videoUrl)) {
      rawMediaList.push(getRes.videoUrl);
    }

    const rawVideos = rawMediaList.filter((item) => isVideoMedia(item));
    const rawImages = rawMediaList.filter((item) => !isVideoMedia(item));

    let list = [];
    if (rawVideos.length > 0) {
      const mainVideo = rawVideos[0];
      const firstImg = rawImages[0] || null;
      const remainingImages = rawImages.slice(1);
      const extraVideos = rawVideos.slice(1);

      if (firstImg) {
        list.push({ type: "image", url: getOptimizedImageUrl(firstImg, { width: 1200, quality: "auto:good", colorSpace: "srgb" }), thumbUrl: getOptimizedImageUrl(firstImg, { width: 220, height: 280, crop: "fill", colorSpace: "srgb" }) });
        list.push({ type: "video", url: getOptimizedVideoUrl(mainVideo, { width: 720 }) });
        remainingImages.forEach((img) => list.push({ type: "image", url: getOptimizedImageUrl(img, { width: 1200, quality: "auto:good", colorSpace: "srgb" }), thumbUrl: getOptimizedImageUrl(img, { width: 220, height: 280, crop: "fill", colorSpace: "srgb" }) }));
        extraVideos.forEach((vid) => list.push({ type: "video", url: getOptimizedVideoUrl(vid, { width: 720 }) }));
      } else {
        list.push({ type: "video", url: getOptimizedVideoUrl(mainVideo, { width: 720 }) });
        extraVideos.forEach((vid) => list.push({ type: "video", url: getOptimizedVideoUrl(vid, { width: 720 }) }));
      }
    } else {
      list = rawImages.map((img) => ({ type: "image", url: getOptimizedImageUrl(img, { width: 1200, quality: "auto:good", colorSpace: "srgb" }), thumbUrl: getOptimizedImageUrl(img, { width: 220, height: 280, crop: "fill", colorSpace: "srgb" }) }));
    }
    return list;
  }, [getRes]);

  const images = React.useMemo(() => mediaList.map((m) => m.url), [mediaList]);

  const api = axios.create({
    baseURL: API_BASE_URL,
    withCredentials: true,
  });

  api.interceptors.request.use((config) => {
    const token = localStorage.getItem("token");
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  });

  const refreshToken = async () => {
    const newToken = await refreshAccessToken();
    if (!newToken) {
      setIsLoggedIn(false);
      setUserId(null);
      setUserFirstName("");
      return false;
    }
    const payload = decodeJwtPayload(newToken);
    if (payload?.userId) {
      setIsLoggedIn(true);
      setUserId(payload.userId);
      setUserFirstName(payload.firstName || "");
      return true;
    }
    return false;
  };

  const keepAlive = async () => {
    try {
      const response = await api.get("/ping");
    } catch (error) {
      // Attempt to refresh token on 401/403
      if (error.response?.status === 401 || error.response?.status === 403) {
        const refreshed = await refreshToken();
        if (refreshed) {
          try {
            const retryResponse = await api.get("/ping");
          } catch (retryError) {}
        }
      }
    }
  };

  const fetchUserInfo = async () => {
    try {
      const response = await api.get("/auth/me");
      setUserFirstName(response.data.firstName || "");
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        const refreshed = await refreshToken();
        if (refreshed) {
          fetchUserInfo();
        }
      }
    }
  };

  const fetchCartItemCount = async (userId) => {
    try {
      const response = await api.get(`/cart/user/${userId}`);
      // Backend wraps response: { success, data: [...] } or plain array
      const cartData = response.data?.data ?? response.data;
      const count = Array.isArray(cartData) ? cartData.length : 0;
      setCartItemCount(count);
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        const refreshed = await refreshToken();
        if (refreshed) {
          const retryResponse = await api.get(`/cart/user/${userId}`);
          const retryData = retryResponse.data?.data ?? retryResponse.data;
          const count = Array.isArray(retryData) ? retryData.length : 0;
          setCartItemCount(count);
        }
      }
    }
  };

  useEffect(() => {
    const jwtCookie = getCookie("jwt");
    let token = jwtCookie;

    if (jwtCookie) {
      localStorage.setItem("token", jwtCookie);
    } else {
      token = localStorage.getItem("token");
    }

    if (token) {
      if (isTokenExpired(token)) {
        refreshToken().then((refreshed) => {
          if (refreshed) {
            const newToken = localStorage.getItem("token");
            const payload = decodeJwtPayload(newToken);
            if (payload?.userId) {
              setIsLoggedIn(true);
              setUserId(payload.userId);
              setUserFirstName(payload.firstName || "");
              fetchUserInfo();
              fetchCartItemCount(payload.userId);
            }
          } else {
            setIsLoggedIn(false);
            setUserId(null);
            localStorage.removeItem("token");
            localStorage.removeItem("isLoggedIn");
            localStorage.removeItem("userId");
            localStorage.removeItem("userFirstName");
          }
        });
      } else {
        const payload = decodeJwtPayload(token);
        if (payload?.userId) {
          setIsLoggedIn(true);
          setUserId(payload.userId);
          setUserFirstName(payload.firstName || "");
          fetchUserInfo();
          fetchCartItemCount(payload.userId);
        } else {
          setIsLoggedIn(false);
          setUserId(null);
          localStorage.removeItem("token");
          localStorage.removeItem("isLoggedIn");
          localStorage.removeItem("userId");
          localStorage.removeItem("userFirstName");
        }
      }
    } else {
      setIsLoggedIn(false);
      setUserId(null);
      localStorage.removeItem("token");
      localStorage.removeItem("isLoggedIn");
      localStorage.removeItem("userId");
      localStorage.removeItem("userFirstName");
      setCartItemCount(getGuestCartCount());
    }

    const onGuestCartUpdate = () => setCartItemCount(getGuestCartCount());
    window.addEventListener("aminii-guest-cart-updated", onGuestCartUpdate);

    const keepAliveInterval = setInterval(() => {
      if (localStorage.getItem("isLoggedIn") === "true") {
        keepAlive();
      }
    }, 600000); // Changed to 5 minutes for more frequent checks

    return () => {
      clearInterval(keepAliveInterval);
      window.removeEventListener("aminii-guest-cart-updated", onGuestCartUpdate);
    };
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [useLocation().pathname]);

  useEffect(() => {
    const handleResize = () => setIsMobileView(window.innerWidth <= 768);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (window.innerWidth <= 768) {
        setShowStickyMobileBar(window.scrollY > 350);
      } else {
        setShowStickyMobileBar(false);
      }
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (!isMobileView || isSliderSwiping || !mediaList?.length) return;
    const interval = setInterval(() => {
      setCurrentSlide((prev) =>
        prev === (mediaList.length || 1) - 1 ? 0 : prev + 1
      );
    }, 10000);
    return () => clearInterval(interval);
  }, [isMobileView, isSliderSwiping, mediaList]);

  const getFirstImage = (product) => {
    const list = product?.productImages || [];
    const firstImg = list.find(
      (img) => typeof img === "string" && img.startsWith("http") && !isVideoMedia(img)
    );
    if (firstImg) return firstImg;
    const fallback = list[0] || "";
    if (fallback && typeof fallback === "string" && fallback.startsWith("http") && !isVideoMedia(fallback)) {
      return fallback;
    }
    return "https://placehold.co/50x50?text=No+Image";
  };

  const formatPrice = (product) => {
    const productPrice = product?.productPrice || product?.productPrice;
    const beforeDiscountPrice = product?.beforeDiscountPrice;

    if (!productPrice && !beforeDiscountPrice) {
      return {
        formattedPrice: "Price Not Available",
        formattedBeforeDiscountPrice: null,
        discountPercent: null,
        diffAmount: null,
      };
    }

    const price = productPrice ? parseFloat(productPrice) : null;
    const beforePrice = beforeDiscountPrice ? parseFloat(beforeDiscountPrice) : null;

    const formattedPrice = price ? `₹${price.toLocaleString("en-IN")}` : null;
    const formattedBeforeDiscountPrice = beforePrice
      ? `₹${beforePrice.toLocaleString("en-IN")}`
      : null;

    let discountPercent = null;
    let diffAmount = null;
    if (price && beforePrice && beforePrice > price) {
      const pct = Math.round(((beforePrice - price) / beforePrice) * 100);
      const diff = Math.round(beforePrice - price);
      discountPercent = `${pct}% Off`;
      // diffAmount = `Save ₹${diff.toLocaleString("en-IN")}`;
    }

    return { formattedPrice, formattedBeforeDiscountPrice, discountPercent, diffAmount };
  };


  const fetchProductLikeStatus = async (pId) => {
    const token = localStorage.getItem("token");
    if (!token || !pId) return;
    try {
      const response = await axios.get(`${API_BASE_URL}/likes/user/status`, {
        params: { productIds: pId },
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true,
      });
      const likesData = response.data?.data || response.data || {};
      if (likesData[pId] !== undefined) {
        setIsProductLiked(Boolean(likesData[pId]));
      }
    } catch (e) {
      // Graceful fallback
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const productResponse = await api.get(`/products/${productId}`);
        const rawProduct = productResponse.data?.data || productResponse.data;
        const productData = {
          ...rawProduct,
          ...formatPrice(rawProduct),
        };
        setRes(productData);

        if (Array.isArray(rawProduct.productSize) && rawProduct.productSize.length > 0) {
          const firstInStock = rawProduct.productSize.find((s) => (Number(s.stock) || 0) > 0);
          if (firstInStock) {
            setSelectedSize(firstInStock.sizeName);
          } else {
            setSelectedSize(rawProduct.productSize[0].sizeName);
          }
        }

        if (localStorage.getItem("token")) {
          fetchProductLikeStatus(productId);
        }

        const isMainImg = (cat) =>
          cat?.categoryName?.toLowerCase().replace(/[-_\s]+/g, "") === "mainimage" ||
          cat?.subCategory?.categoryName?.toLowerCase().replace(/[-_\s]+/g, "") === "mainimage";

        // Helper to detect category key for recommendations
        const getCategoryKey = (prod) => {
          if (!prod) return null;
          const cats = prod.productCategory || [];
          const catNames = cats.map((c) => (c.categoryName || "").toLowerCase().trim());
          const subCatNames = cats.map((c) => (c.subCategory?.categoryName || "").toLowerCase().trim());
          const tags = (prod.tags || []).map((t) => (t || "").toLowerCase().trim());
          const allNames = [...catNames, ...subCatNames, ...tags];

          for (const name of allNames) {
            if (
              name.includes("basic") ||
              name.includes("cotton") ||
              name.includes("daily wear") ||
              name.includes("casual set") ||
              name.includes("top")
            ) {
              return "basics";
            }
            if (
              name.includes("best") ||
              name.includes("seller") ||
              name.includes("trending") ||
              name.includes("featured") ||
              name.includes("festive")
            ) {
              return "bestseller";
            }
            if (
              name.includes("luxe") ||
              name.includes("pakistani") ||
              name.includes("cordset") ||
              name.includes("premium")
            ) {
              return "aminiiluxe";
            }
            if (
              name.includes("plus") ||
              name.includes("4xl") ||
              name.includes("5xl") ||
              name.includes("6xl")
            ) {
              return "aminiiplus";
            }
          }

          const pText = `${prod.productName || ""} ${prod.description || ""}`.toLowerCase();
          if (pText.includes("basic") || pText.includes("cotton")) return "basics";
          if (pText.includes("best seller") || pText.includes("bestseller") || pText.includes("trending")) return "bestseller";
          if (pText.includes("luxe") || pText.includes("pakistani")) return "aminiiluxe";
          if (pText.includes("plus") || pText.includes("4xl") || pText.includes("5xl") || pText.includes("6xl")) return "aminiiplus";

          return "basics";
        };

        // Determine target recommendation category based on user flow:
        // BASICS -> Best seller
        // Best seller -> Aminii luxe
        // Aminii luxe -> Basics
        // plus sizes -> Best seller
        const currentCatKey = getCategoryKey(rawProduct);
        let targetCatKey = "bestseller";
        if (currentCatKey === "basics") {
          targetCatKey = "bestseller";
        } else if (currentCatKey === "bestseller") {
          targetCatKey = "aminiiluxe";
        } else if (currentCatKey === "aminiiluxe") {
          targetCatKey = "basics";
        } else if (currentCatKey === "aminiiplus") {
          targetCatKey = "bestseller";
        }

        // Fetch all products from DB for cross-category recommendations
        const allProductsResponse = await api.get(`/products`);
        const allProducts =
          allProductsResponse.data?.data ||
          (Array.isArray(allProductsResponse.data) ? allProductsResponse.data : []);

        const validCandidates = allProducts.filter(
          (p) =>
            p.productId !== parseInt(productId, 10) &&
            !p.productCategory?.some(isMainImg)
        );

        // 1. Primary recommendations from the target category
        const targetProducts = validCandidates.filter(
          (p) => getCategoryKey(p) === targetCatKey
        );

        // 2. Fallback products to ensure recommendations are always populated
        const targetIds = new Set(targetProducts.map((p) => p.productId));
        const otherProducts = validCandidates.filter((p) => !targetIds.has(p.productId));

        const finalRecommendations = [...targetProducts, ...otherProducts]
          .slice(0, 8)
          .map((p) => ({
            ...p,
            ...formatPrice(p),
            displayImage: getFirstImage(p),
          }));

        setCategoryProdu(finalRecommendations);
        setLoading(false);
      } catch (error) {
        setError(error.response?.data?.message || error.message);
        setLoading(false);
      }
    };

    if (productId) fetchData();
    else {
      setError("No product ID found in URL");
      setLoading(false);
    }
  }, [productId]);

  const handleImageModalPrev = () => {
    setSelectedImageIndex((prev) =>
      prev === 0 ? (mediaList.length || 1) - 1 : prev - 1
    );
  };

  const handleImageModalNext = () => {
    setSelectedImageIndex((prev) =>
      prev === (mediaList.length || 1) - 1 ? 0 : prev + 1
    );
  };

  const handleImageClick = (index) => {
    setSelectedImageIndex(index);
    setIsImageModalOpen(true);
  };

  const handleLogoutCleanup = () => {
    setIsLoggedIn(false);
    setUserFirstName("");
    setUserId(null);
    setCartItemCount(0);
  };

  const handleAddToCart = async (options = {}) => {
    const isBuyNow = !!options.isBuyNow;
    if (isAddingToCart) return;

    if (!selectedSize) {
      setModalType("message");
      setModalMessage("Please select a size before adding to cart.");
      setIsModalOpen(true);
      return false;
    }

    const currentSizeObj = getRes?.productSize?.find((s) => s.sizeName === selectedSize);
    const stockVal = currentSizeObj?.stock ?? (getRes?.stock ?? null);
    if (stockVal !== null && stockVal !== undefined && Number(stockVal) <= 0) {
      setModalType("message");
      setModalMessage(`Size ${selectedSize} is currently out of stock.`);
      setIsModalOpen(true);
      return false;
    }

    setIsAddingToCart(true);

    const token = localStorage.getItem("token");
    // Use localStorage userId as authoritative fallback — state may not have resolved yet
    const resolvedUserId = userId || localStorage.getItem("userId");
    const loggedIn = resolvedUserId && token && !isTokenExpired(token);

    if (!loggedIn) {
      const firstValidImg =
        images?.[0] ||
        getFirstImage(getRes) ||
        getRes?.productImages?.find((img) => typeof img === "string" && !isVideoMedia(img)) ||
        getRes?.productImages?.[0] ||
        "";

      addGuestCartItem(
        {
          productId: parseInt(productId, 10),
          productName: getRes?.productName || "Product",
          productImage: firstValidImg,
          productPrice: getRes?.productPrice,
          beforeDiscountPrice:
            getRes?.beforeDiscountPrice || getRes?.productPrice,
          size: selectedSize,
          quantity: 1,
          sku: getRes?.sku || `SKU-${productId}`,
          hsn: getRes?.hsn || "6204",
        },
        { preventDuplicate: isBuyNow }
      );
      setCartItemCount(getGuestCartCount());
      setIsAddingToCart(false);

      if (!isBuyNow) {
        // Open modern slide Cart Drawer instead of old popup modal
        window.dispatchEvent(new CustomEvent("aminii-open-cart-drawer"));
      }
      return true;
    }

    if (isTokenExpired(token)) {
      const refreshed = await refreshToken();
      if (!refreshed) {
        setModalType("loginPrompt");
        setModalMessage("Session expired. Please log in again.");
        setIsModalOpen(true);
        setIsAddingToCart(false);
        return false;
      }
    }

    try {
      let cartItems = [];
      try {
        const cartResponse = await api.get(`/cart/user/${resolvedUserId}`);
        // Backend wraps: { success: true, data: [...] }
        const raw = cartResponse.data?.data ?? cartResponse.data;
        cartItems = Array.isArray(raw) ? raw : [];
      } catch (error) {
        if (error.response?.status === 404) {
          cartItems = [];
        } else {
          throw error;
        }
      }

      const existingItem = cartItems.find(
        (item) =>
          item.productId === parseInt(productId) &&
          item.cartSize === selectedSize
      );

      if (existingItem) {
        // If Buy Now: item is already in cart, do NOT increment again!
        if (!isBuyNow) {
          const updatePayload = {
            userId: parseInt(resolvedUserId),
            productId: parseInt(productId),
            cartSize: selectedSize,
            cartQty: existingItem.cartQty + 1,
          };
          await api.put(`/cart/${existingItem.cartId}`, updatePayload);
        }
      } else {
        const newPayload = {
          userId: parseInt(resolvedUserId),
          productId: parseInt(productId),
          cartSize: selectedSize,
          cartQty: 1,
        };
        await api.post(`/cart`, newPayload);
      }

      await fetchCartItemCount(resolvedUserId);
      setIsAddingToCart(false);

      if (!isBuyNow) {
        // Open modern slide Cart Drawer instead of old popup modal
        window.dispatchEvent(new CustomEvent("aminii-open-cart-drawer"));
      }
      return true;
    } catch (error) {
      let errorMessage = "Failed to add to cart. Please try again.";
      if (error.response?.status === 403 || error.response?.status === 401) {
        const refreshed = await refreshToken();
        if (refreshed) {
          setIsAddingToCart(false);
          return handleAddToCart(options); // Retry the entire operation
        }
        errorMessage = "Session expired or access denied. Please log in again.";
      } else if (error.response?.data?.message) {
        errorMessage = `Failed to add to cart: ${error.response.data.message}`;
      }
      setModalType("message");
      setModalMessage(errorMessage);
      setIsModalOpen(true);
      setIsAddingToCart(false);
      return false;
    }
  };

  const handleBuyNow = async () => {
    const success = await handleAddToCart({ isBuyNow: true });
    if (success) {
      navigate("/checkout");
    }
  };

  const generateWhatsAppLink = () => {
    return "https://wa.me/9606132902";
  };

  const handleClickEachProd = (newProductId) => {
    navigate(`/productDetails/${newProductId}`);
  };

  const handleNext = () => {
    if (startIndex + (isMobileView ? 1 : 1) < categoryProdu.length) {
      setStartIndex(startIndex + (isMobileView ? 1 : 1));
      scrollCarousel(1);
    }
  };

  const handlePrev = () => {
    if (startIndex - (isMobileView ? 1 : 1) >= 0) {
      setStartIndex(startIndex - (isMobileView ? 1 : 1));
      scrollCarousel(-1);
    }
  };

  const scrollCarousel = (direction) => {
    if (carouselRef.current) {
      const scrollAmount =
        direction * (carouselRef.current.offsetWidth / (isMobileView ? 2 : 4));
      carouselRef.current.scrollBy({
        left: scrollAmount,
        behavior: "smooth",
      });
    }
  };

  const handleMouseDown = (e) => {
    setIsDragging(true);
    setStartX(e.pageX - carouselRef.current.offsetLeft);
    setScrollLeft(carouselRef.current.scrollLeft);
  };

  const handleTouchStartCarousel = (e) => {
    setIsDragging(true);
    setStartX(e.touches[0].pageX - carouselRef.current.offsetLeft);
    setScrollLeft(carouselRef.current.scrollLeft);
    document.body.classList.add("no-scroll");
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    const x = e.pageX - carouselRef.current.offsetLeft;
    const walk = (x - startX) * 2;
    const maxScroll =
      carouselRef.current.scrollWidth - carouselRef.current.clientWidth;
    let newScrollLeft = scrollLeft - walk;
    if (newScrollLeft < 0) newScrollLeft = 0;
    if (newScrollLeft > maxScroll) newScrollLeft = maxScroll;
    carouselRef.current.scrollLeft = newScrollLeft;
  };

  const handleTouchMoveCarousel = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    e.stopPropagation();
    const x = e.touches[0].pageX - carouselRef.current.offsetLeft;
    const walk = (x - startX) * 2;
    const maxScroll =
      carouselRef.current.scrollWidth - carouselRef.current.clientWidth;
    let newScrollLeft = scrollLeft - walk;
    if (newScrollLeft < 0) newScrollLeft = 0;
    if (newScrollLeft > maxScroll) newScrollLeft = maxScroll;
    carouselRef.current.scrollLeft = newScrollLeft;
  };

  const handleDragEnd = () => {
    setIsDragging(false);
    document.body.classList.remove("no-scroll");
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setModalType("message");
    setModalMessage("");
  };

  const handleImageError = (e) => {
    e.target.src = isMobileView
      ? "https://placehold.co/300x300?text=Image+Not+Available"
      : "https://placehold.co/250x250?text=Image+Not+Available";
    e.target.classList.add("amin-image-error");
    e.target.onerror = null;
  };

  if (loading)
    return <div className="pd-loading" style={{ padding: "32px 24px", maxWidth: 1200, margin: "0 auto" }}><SkeletonDetail /></div>;
  if (error)
    return <AminiiErrorPage message="We couldn't find this product. It may have been removed or the link is incorrect." showHomeButton={true} />;
  if (!getRes)
    return <AminiiErrorPage message="This product doesn't exist or is no longer available." showHomeButton={true} />;

  const handleSliderTouchStart = (e) => {
    if (!mediaList || mediaList.length <= 1) return;
    setSliderTouchStartX(e.touches[0].clientX);
    setSliderTouchDeltaX(0);
    setIsSliderSwiping(true);
  };

  const handleSliderTouchMove = (e) => {
    if (!isSliderSwiping) return;
    const currentX = e.touches[0].clientX;
    const delta = currentX - sliderTouchStartX;
    setSliderTouchDeltaX(delta);
  };

  const handleSliderTouchEnd = () => {
    if (!isSliderSwiping) return;
    setIsSliderSwiping(false);
    if (sliderTouchDeltaX < -40 && currentSlide < mediaList.length - 1) {
      setCurrentSlide((prev) => prev + 1);
    } else if (sliderTouchDeltaX > 40 && currentSlide > 0) {
      setCurrentSlide((prev) => prev - 1);
    }
    setSliderTouchDeltaX(0);
  };

  return (
    <div className="amin-dashboard">
      {isModalOpen && (
        <div className="pd-modal-overlay">
          <div className="pd-modal">
            <button className="pd-modal-close-x" onClick={closeModal}>
              ×
            </button>
            {modalType === "message" && (
              <>
                <p>{modalMessage}</p>
                <button className="pd-modal-close" onClick={closeModal}>
                  OK
                </button>
              </>
            )}
            {modalType === "loginPrompt" && (
              <>
                <p>{modalMessage}</p>
                <div className="pd-modal-buttons">
                  <button
                    onClick={() => {
                      navigate("/login");
                      closeModal();
                    }}
                  >
                    Login
                  </button>
                  <button
                    onClick={() => {
                      navigate("/register");
                      closeModal();
                    }}
                  >
                    Register
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {isImageModalOpen && (
        <div
          className="pd-image-modal-overlay"
          onClick={() => setIsImageModalOpen(false)}
        >
          <div
            className="pd-image-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="pd-image-modal-close"
              onClick={() => setIsImageModalOpen(false)}
            >
              ×
            </button>
            <button
              className="pd-image-modal-nav pd-image-modal-prev"
              onClick={(e) => { e.stopPropagation(); handleImageModalPrev(); }}
            >
              ❮
            </button>
            {mediaList[selectedImageIndex]?.type === "video" ? (
              <video
                src={mediaList[selectedImageIndex]?.url}
                className="pd-image-modal-img pd-modal-video"
                playsInline
                autoPlay
                muted
                loop
                preload="auto"
              />
            ) : (
              <img
                src={
                  mediaList[selectedImageIndex]?.url ||
                  "https://placehold.co/600x600?text=Image+Not+Available"
                }
                alt={`${getRes.productName || "Product"} ${
                  selectedImageIndex + 1
                }`}
                className="pd-image-modal-img"
                onError={handleImageError}
              />
            )}
            <button
              className="pd-image-modal-nav pd-image-modal-next"
              onClick={(e) => { e.stopPropagation(); handleImageModalNext(); }}
            >
              ❯
            </button>
          </div>
        </div>
      )}

      <AminiiNavbar
        isLoggedIn={isLoggedIn}
        userFirstName={userFirstName}
        cartItemCount={cartItemCount}
        onLogout={handleLogoutCleanup}
      />

      <div className="pd-container">
        {isMobileView ? (
          <div className="pd-image-slider" ref={sliderRef}>
            {getRes && (
              <LikeButton
                productId={getRes.productId}
                initialCount={getRes.likeCount || 0}
                isLikedByUser={isProductLiked}
                style={{ top: "14px", right: "14px", zIndex: 12 }}
              />
            )}
            {mediaList.length > 0 ? (
              <div
                className="pd-image-slider-inner"
                style={{
                  transform: `translateX(calc(${isSliderSwiping ? sliderTouchDeltaX + "px + " : ""}10vw - ${currentSlide * 80}vw - ${currentSlide * 12}px))`,
                  transition: isSliderSwiping ? "none" : "transform 0.35s cubic-bezier(0.25, 1, 0.5, 1)",
                }}
                onTouchStart={handleSliderTouchStart}
                onTouchMove={handleSliderTouchMove}
                onTouchEnd={handleSliderTouchEnd}
              >
                {mediaList.map((item, index) => {
                  const isCurrent = index === currentSlide;
                  return (
                    <div
                      key={`mobile-slide-${index}`}
                      className={`pd-image-slide-item ${isCurrent ? "active-slide" : "inactive-slide"}`}
                      onClick={() => setCurrentSlide(index)}
                    >
                      {item.type === "video" ? (
                        <div className="pd-mobile-video-wrap">
                          <VideoSlide
                            src={item.url}
                            isCurrent={isCurrent}
                            className="pd-image-slider-img"
                            extraClassName="pd-video-element"
                          />
                          <div className="pd-video-badge">
                            <span className="pd-video-badge-icon">▶</span>
                            <span>Video</span>
                          </div>
                        </div>
                      ) : (
                        <img
                          src={item.url}
                          alt={`${getRes.productName || "Product"} ${index + 1}`}
                          className="pd-image-slider-img"
                          onError={handleImageError}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="pd-image-slide-item active-slide" style={{ margin: "0 auto" }}>
                <img
                  src="https://placehold.co/300x300?text=Image+Not+Available"
                  alt="No images available"
                  className="pd-image-slider-img"
                />
              </div>
            )}
            {/* Kaftan.ae style slide indicator dots — only shown when there are multiple media items */}
            {mediaList.length > 1 && (
              <div className="pd-slider-dots">
                {mediaList.map((item, index) => (
                  <button
                    key={`dot-${index}`}
                    className={`pd-slider-dot ${index === currentSlide ? "active" : ""} ${item.type === "video" ? "pd-video-dot" : ""}`}
                    onClick={() => setCurrentSlide(index)}
                    aria-label={`Go to slide ${index + 1}${item.type === "video" ? " (Video)" : ""}`}
                  >
                    {item.type === "video" && <span className="pd-dot-video-icon">▶</span>}
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className={`pd-image-grid ${mediaList.length === 1 ? "pd-image-grid-single" : ""}`}>
            {mediaList.length > 0 ? (
              mediaList.map((item, index) => (
                <div key={`desktop-media-wrap-${index}`} style={{ position: "relative", width: "100%" }}>
                  {index === 0 && getRes && (
                    <LikeButton
                      productId={getRes.productId}
                      initialCount={getRes.likeCount || 0}
                      isLikedByUser={isProductLiked}
                      style={{ top: "14px", right: "14px" }}
                    />
                  )}
                  {item.type === "video" ? (
                    <div className="pd-video-panel-wrapper">
                      <div className="pd-video-panel">
                        <VideoSlide
                          src={item.url}
                          isCurrent={true}
                          className="pd-video-element"
                        />
                        <div className="pd-video-badge">
                          <span className="pd-video-badge-icon">▶</span>
                          <span>Video</span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <img
                      src={item.url}
                      alt={`${getRes.productName || "Product"} ${index + 1}`}
                      className="pd-image-grid-img"
                      onError={handleImageError}
                      onClick={() => handleImageClick(index)}
                    />
                  )}
                </div>
              ))
            ) : (
              <div style={{ position: "relative", width: "100%" }}>
                {getRes && (
                  <LikeButton
                    productId={getRes.productId}
                    initialCount={getRes.likeCount || 0}
                    isLikedByUser={isProductLiked}
                    style={{ top: "14px", right: "14px" }}
                  />
                )}
                <img
                  src="https://placehold.co/250x250?text=Image+Not+Available"
                  alt="No images available"
                  className="pd-image-grid-img"
                />
              </div>
            )}
          </div>
        )}
        <div className="pd-info">
          <div className="pd-name">{getRes.productName || "Product Name"}</div>
          <div className="pd-price-container">
            <span className="pd-price">
              {getRes.formattedPrice || "Price Not Available"}
            </span>
            {getRes.formattedBeforeDiscountPrice && (
              <s className="pd-before-discount-price">
                {getRes.formattedBeforeDiscountPrice}
              </s>
            )}
            {getRes.discountPercent && (
              <span className="pd-discount-badge">{getRes.discountPercent}</span>
            )}
          </div>
          {/* {getRes.diffAmount && (
            <div className="pd-save-amount">{getRes.diffAmount}</div>
          )}
          <div className="pd-tax-note">Inclusive of all taxes</div> */}
          <div className="pd-size-header-row">
            <span className="pd-size-heading">SELECT SIZE</span>
            <button
              type="button"
              className="pd-size-guide-btn"
              onClick={() => setIsSizeGuideOpen(true)}
            >
              📏 Size Guide
            </button>
          </div>
          <div className="pd-size-buttons">
            {getRes.productSize?.map((prodSize, index) => {
              const isOutOfStock = (Number(prodSize.stock) || 0) <= 0;
              return (
                <button
                  key={`size-${index}`}
                  className={`pd-size-button ${
                    selectedSize === prodSize.sizeName ? "selected" : ""
                  } ${isOutOfStock ? "out-of-stock" : ""}`}
                  onClick={() => setSelectedSize(prodSize.sizeName)}
                  title={isOutOfStock ? `${prodSize.sizeName} (Out of Stock)` : prodSize.sizeName}
                >
                  {prodSize.sizeName}
                </button>
              );
            })}
          </div>

          {/* Low Stock Banner */}
          {(() => {
            const currentSizeObj = getRes.productSize?.find((s) => s.sizeName === selectedSize);
            const stockVal = currentSizeObj?.stock ?? (getRes.stock ?? null);
            if (stockVal !== null && stockVal !== undefined) {
              if (stockVal === 0 && selectedSize) {
                return (
                  <div style={{ color: "#dc2626", fontWeight: "700", fontSize: "13px", margin: "10px 0", background: "rgba(220,38,38,0.08)", padding: "8px 12px", borderRadius: "6px", display: "inline-flex", alignItems: "center", gap: "6px" }}>
                    Out of Stock for size {selectedSize}
                  </div>
                );
              }
              if (stockVal > 0 && stockVal <= 3) {
                return (
                  <div style={{ color: "#dc2626", fontWeight: "700", fontSize: "13px", margin: "10px 0", background: "rgba(220,38,38,0.08)", padding: "8px 12px", borderRadius: "6px", display: "inline-flex", alignItems: "center", gap: "6px" }}>
                    Only {stockVal} left in stock - order soon!
                  </div>
                );
              }
            }
            return null;
          })()}

          {/* Product Description & Specifications Accordions */}
          <div className="pd-description-card">
            <div
              className="pd-description-header"
              onClick={() => setIsDescriptionOpen((prev) => !prev)}
            >
              <span>DESCRIPTION</span>
              <span>{isDescriptionOpen ? "▲" : "▼"}</span>
            </div>
            {isDescriptionOpen && (
              <div className="pd-description-body">
                {getRes.description || "Crafted with precision and designed for contemporary elegance. Features premium fabrics, meticulous stitching, and timeless aesthetic."}
              </div>
            )}
          </div>

          {/* Aminii Brand Trust & Info Card */}
          <div className="pd-trust-card">

            {/* Fast & Free Shipping */}

            <div className="pd-trust-row">
              <div className="pd-trust-icon-col">
                <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className="pd-trust-svg-icon">
                  <rect x="2" y="20" width="38" height="24" rx="3" stroke="#1a1a1a" strokeWidth="3" fill="none"/>
                  <path d="M40 28h10l8 10v6h-18V28z" stroke="#1a1a1a" strokeWidth="3" fill="none" strokeLinejoin="round"/>
                  <circle cx="14" cy="47" r="5" stroke="#1a1a1a" strokeWidth="3" fill="none"/>
                  <circle cx="50" cy="47" r="5" stroke="#1a1a1a" strokeWidth="3" fill="none"/>
                  <path d="M6 26H18M2 32H14" stroke="#1a1a1a" strokeWidth="3" strokeLinecap="round"/>
                </svg>
              </div>
              <div className="pd-trust-content-col">
                <div className="pd-trust-section-title">FAST &amp; FREE SHIPPING</div>
                <p className="pd-trust-para">Free shipping on prepaid orders all over India.</p>
                <p className="pd-trust-para"><strong>Cash on Delivery (COD) available within Karnataka.</strong></p>
                <div className="pd-trust-delivery-row">
                  <span className="pd-trust-delivery-item">
                    <svg viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" className="pd-trust-inline-icon"><path d="M10 2C7.24 2 5 4.24 5 7c0 4.25 5 11 5 11s5-6.75 5-11c0-2.76-2.24-5-5-5zm0 6.5c-.83 0-1.5-.67-1.5-1.5S9.17 5.5 10 5.5s1.5.67 1.5 1.5S10.83 8.5 10 8.5z" fill="#1a1a1a"/></svg>
                    Karnataka: 1–2 days
                  </span>
                  <span className="pd-trust-delivery-sep">|</span>
                  <span className="pd-trust-delivery-item">
                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="pd-trust-inline-icon"><path d="M12 2C8.69 2 6 4.69 6 8c0 5.25 6 14 6 14s6-8.75 6-14c0-3.31-2.69-6-6-6zm0 8c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2z" fill="#1a1a1a"/></svg>
                    Other States: 3–5 days
                  </span>
                </div>
              </div>
            </div>
            <div className="pd-trust-separator" />

            {/* Easy Exchange */}
            <div className="pd-trust-row">
              <div className="pd-trust-icon-col">
                <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className="pd-trust-svg-icon">
                  <path d="M 54.9 26.6 A 23.5 23.5 0 0 0 9.1 25.9" stroke="#1a1a1a" strokeWidth="2.8" strokeLinecap="round" fill="none"/>
                  <polyline points="5.0,18.0 9.1,25.9 17.4,23.2" stroke="#1a1a1a" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                  <path d="M 9.1 37.4 A 23.5 23.5 0 0 0 54.9 38.1" stroke="#1a1a1a" strokeWidth="2.8" strokeLinecap="round" fill="none"/>
                  <polyline points="59.0,46.0 54.9,38.1 46.6,40.8" stroke="#1a1a1a" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                </svg>
              </div>
              <div className="pd-trust-content-col">
                <div className="pd-trust-section-title">EASY EXCHANGE</div>
                <ul className="pd-trust-list">
                  <li>Request within 48 hours of delivery.</li>
                  <li>Product must be unused and in original condition.</li>
                  <li>Share Order ID &amp; photos via WhatsApp.</li>
                </ul>
                <p className="pd-trust-para pd-trust-italic">Subject to availability.</p>
              </div>
            </div>
            <div className="pd-trust-separator" />

            {/* Premium Specifications */}
            <div className="pd-trust-row">
              <div className="pd-trust-icon-col">
                <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className="pd-trust-svg-icon">
                  <rect x="8" y="10" width="48" height="8" rx="2" stroke="#1a1a1a" strokeWidth="3" fill="none"/>
                  <rect x="8" y="22" width="48" height="8" rx="2" stroke="#1a1a1a" strokeWidth="3" fill="none"/>
                  <rect x="8" y="34" width="48" height="8" rx="2" stroke="#1a1a1a" strokeWidth="3" fill="none"/>
                  <rect x="8" y="46" width="48" height="8" rx="2" stroke="#1a1a1a" strokeWidth="3" fill="none"/>
                </svg>
              </div>
              <div className="pd-trust-content-col">
                <div className="pd-trust-section-title">PREMIUM SPECIFICATIONS</div>
                <p className="pd-trust-para">
                  Breathable heavy fabrics &nbsp;|&nbsp; Luxe Fit S–6XL &nbsp;|&nbsp; Hand-inspected finishing.
                </p>
              </div>
            </div>

            {/* Shop With Confidence */}
            <div className="pd-trust-confidence-section">
              <div className="pd-trust-confidence-title">SHOP WITH CONFIDENCE</div>
              <div className="pd-trust-confidence-grid">
                <div className="pd-trust-badge-item">
                  <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className="pd-trust-badge-icon">
                    <polygon points="32,4 40,24 62,24 46,38 52,58 32,46 12,58 18,38 2,24 24,24" stroke="#1a1a1a" strokeWidth="3" fill="none" strokeLinejoin="round"/>
                  </svg>
                  <span>PREMIUM<br/>FABRICS</span>
                </div>
                <div className="pd-trust-badge-item">
                  <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className="pd-trust-badge-icon">
                    <rect x="6" y="20" width="52" height="34" rx="4" stroke="#1a1a1a" strokeWidth="3" fill="none"/>
                    <path d="M6 30h52" stroke="#1a1a1a" strokeWidth="3"/>
                    <path d="M20 39h8M32 39h12" stroke="#1a1a1a" strokeWidth="3" strokeLinecap="round"/>
                    <path d="M32 8l-16 12h32L32 8z" stroke="#1a1a1a" strokeWidth="2.5" fill="none" strokeLinejoin="round"/>
                  </svg>
                  <span>SECURE<br/>PAYMENTS</span>
                </div>
                <div className="pd-trust-badge-item">
                  <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className="pd-trust-badge-icon">
                    <rect x="2" y="20" width="38" height="24" rx="3" stroke="#1a1a1a" strokeWidth="3" fill="none"/>
                    <path d="M40 28h10l8 10v6h-18V28z" stroke="#1a1a1a" strokeWidth="3" fill="none" strokeLinejoin="round"/>
                    <circle cx="14" cy="47" r="5" stroke="#1a1a1a" strokeWidth="3" fill="none"/>
                    <circle cx="50" cy="47" r="5" stroke="#1a1a1a" strokeWidth="3" fill="none"/>
                  </svg>
                  <span>FREE<br/>SHIPPING</span>
                </div>
                <div className="pd-trust-badge-item">
                  <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className="pd-trust-badge-icon">
                    <path d="M 54.9 26.6 A 23.5 23.5 0 0 0 9.1 25.9" stroke="#1a1a1a" strokeWidth="2.8" strokeLinecap="round" fill="none"/>
                    <polyline points="5.0,18.0 9.1,25.9 17.4,23.2" stroke="#1a1a1a" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                    <path d="M 9.1 37.4 A 23.5 23.5 0 0 0 54.9 38.1" stroke="#1a1a1a" strokeWidth="2.8" strokeLinecap="round" fill="none"/>
                    <polyline points="59.0,46.0 54.9,38.1 46.6,40.8" stroke="#1a1a1a" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                  </svg>
                  <span>EASY<br/>EXCHANGE</span>
                </div>
                <div className="pd-trust-badge-item">
                  <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className="pd-trust-badge-icon">
                    <path d="M32 6C17.64 6 6 17.64 6 32c0 4.84 1.37 9.36 3.76 13.17L6 58l13.15-3.7C22.8 56.64 27.27 58 32 58c14.36 0 26-11.64 26-26S46.36 6 32 6z" stroke="#1a1a1a" strokeWidth="3" fill="none" strokeLinejoin="round"/>
                    <path d="M24 27c0-4.42 3.58-8 8-8 4.42 0 8 3.58 8 8 0 5.5-8 14-8 14s-8-8.5-8-14z" stroke="#1a1a1a" strokeWidth="2.5" fill="none"/>
                  </svg>
                  <span>WHATSAPP<br/>SUPPORT</span>
                </div>
              </div>
            </div>

            {/* WhatsApp CTA */}
            <a
              href="https://wa.me/9606132902"
              target="_blank"
              rel="noopener noreferrer"
              className="pd-trust-wa-btn"
            >
              <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className="pd-trust-wa-icon">
                <path d="M32 6C17.64 6 6 17.64 6 32c0 4.84 1.37 9.36 3.76 13.17L6 58l13.15-3.7C22.8 56.64 27.27 58 32 58c14.36 0 26-11.64 26-26S46.36 6 32 6z" fill="#25D366"/>
                <path d="M43.5 37.5c-.7-.35-4.15-2.05-4.8-2.3-.65-.25-1.12-.35-1.58.35-.47.7-1.82 2.3-2.23 2.77-.41.47-.82.53-1.52.18-4.15-2.08-6.88-3.7-9.6-8.38-.72-1.25.72-1.16 2.06-3.84.23-.47.12-.88-.06-1.23-.18-.35-1.58-3.82-2.17-5.22-.57-1.37-1.16-1.18-1.58-1.2-.41-.02-.88-.02-1.35-.02s-1.23.18-1.88.88c-.65.7-2.47 2.42-2.47 5.88s2.53 6.83 2.88 7.3c.35.47 4.97 7.6 12.05 10.66 7.08 3.06 7.08 2.04 8.35 1.91 1.27-.12 4.1-1.67 4.68-3.29.58-1.61.58-2.99.41-3.29-.18-.29-.65-.47-1.35-.82z" fill="white"/>
              </svg>
              <div className="pd-trust-wa-text">
                <span className="pd-trust-wa-label">WhatsApp Support</span>
                <span className="pd-trust-wa-number">+91 96061 32902</span>
              </div>
              <span className="pd-trust-wa-arrow">›</span>
            </a>
            <p className="pd-trust-footer-note">
              For product details, size assistance &amp; order support,<br />feel free to contact us.
            </p>
            <div className="pd-trust-final-divider" />
            <p className="pd-trust-final-tagline">Your trust matters to us ♥</p>
          </div>
        </div>


        <div className="pd-recommendations">
          <p className="pd-recommendations-title">YOU MAY ALSO LIKE</p>
          {categoryProdu?.length > 0 ? (
            <div className="amin-carousel-container">
              <div className="amin-carousel-controls">
                <button onClick={handlePrev} disabled={startIndex === 0}>
                  ❮
                </button>
                <button
                  onClick={handleNext}
                  disabled={startIndex + (isMobileView ? 2 : 4) >= categoryProdu.length}
                >
                  ❯
                </button>
              </div>
              <div
                className="amin-product-grid"
                ref={carouselRef}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleDragEnd}
                onMouseLeave={handleDragEnd}
                onTouchStart={handleTouchStartCarousel}
                onTouchMove={handleTouchMoveCarousel}
                onTouchEnd={handleDragEnd}
              >
                {categoryProdu.map((product, idx) => {
                  const pSizes = product.productSize || product.ProductSize || [];
                  const isProdSoldOut = Array.isArray(pSizes) && pSizes.length > 0
                    ? pSizes.every((s) => (Number(s.stock) || 0) <= 0)
                    : (product.stock !== undefined && product.stock !== null ? Number(product.stock) <= 0 : false);

                  return (
                    <div
                      key={`product-${product.productId || idx}`}
                      className="amin-product-item"
                      onClick={() => product.productId && handleClickEachProd(product.productId)}
                    >
                      <div className="amin-product-img-wrap" style={{ position: "relative" }}>
                        {isProdSoldOut && <span className="pl-sold-out-badge">Sold Out</span>}
                        <img
                          className="productImages"
                          src={product.displayImage || getFirstImage(product)}
                          alt={product.productName || "Product"}
                          onError={handleImageError}
                        />
                      </div>
                      <p>{product.productName}</p>
                      <div className="pd-price-container">
                        <span className="pd-price">
                          {product.formattedPrice || product.price}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <p className="no-similar-products">No similar products found</p>
          )}
        </div>
      </div>

      <FreeShipping />
      <AminiiFooter />
      {(() => {
        const currentSizeObj = getRes?.productSize?.find((s) => s.sizeName === selectedSize);
        const stockVal = currentSizeObj?.stock ?? (getRes?.stock ?? null);
        const allSizesOOS = Array.isArray(getRes?.productSize) && getRes.productSize.length > 0
          ? getRes.productSize.every((s) => (Number(s.stock) || 0) <= 0)
          : (getRes?.stock !== undefined && getRes?.stock !== null ? Number(getRes.stock) <= 0 : false);
        const isCurrentOOS = selectedSize ? (stockVal !== null && Number(stockVal) <= 0) : allSizesOOS;

        return (
          <div className="addtocart-buynow">
            <button
              className={`addCart ${isCurrentOOS ? "sold-out" : ""}`}
              onClick={handleAddToCart}
              disabled={isAddingToCart || isCurrentOOS}
            >
              {isCurrentOOS ? "SOLD OUT" : isAddingToCart ? "Adding..." : "ADD TO CART"}
            </button>
            <button
              className={`buyNow ${isCurrentOOS ? "disabled" : ""}`}
              onClick={handleBuyNow}
              disabled={isAddingToCart || isCurrentOOS}
            >
              BUY NOW
            </button>
          </div>
        );
      })()}

      {/* Sticky Floating Mobile CTA Bar */}
      {isMobileView && showStickyMobileBar && getRes && (() => {
        const currentSizeObj = getRes.productSize?.find((s) => s.sizeName === selectedSize);
        const stockVal = currentSizeObj?.stock ?? (getRes.stock ?? null);
        const allSizesOOS = Array.isArray(getRes.productSize) && getRes.productSize.length > 0
          ? getRes.productSize.every((s) => (Number(s.stock) || 0) <= 0)
          : (getRes?.stock !== undefined && getRes?.stock !== null ? Number(getRes.stock) <= 0 : false);
        const isCurrentOOS = selectedSize ? (stockVal !== null && Number(stockVal) <= 0) : allSizesOOS;

        return (
          <div className="pd-sticky-mobile-bar">
            <div className="pd-sticky-left">
              <img
                src={images[0] || getFirstImage(getRes)}
                alt={getRes.productName}
                className="pd-sticky-thumb"
                onError={handleImageError}
              />
              <div className="pd-sticky-info">
                <span className="pd-sticky-title">{getRes.productName}</span>
                <div className="pd-sticky-sub">
                  <span className="pd-sticky-price">{getRes.formattedPrice || ("₹" + getRes.productPrice)}</span>
                  {selectedSize && <span className="pd-sticky-size-tag">Size: {selectedSize}</span>}
                </div>
              </div>
            </div>
            <button
              className={`pd-sticky-btn ${isCurrentOOS ? "sold-out" : ""}`}
              onClick={handleAddToCart}
              disabled={isAddingToCart || isCurrentOOS}
            >
              {isCurrentOOS ? "Sold Out" : isAddingToCart ? "Adding..." : "Add to Bag"}
            </button>
          </div>
        );
      })()}

      {/* Interactive Size Guide Modal */}
      {isSizeGuideOpen && (
        <div className="pd-size-guide-backdrop" onClick={() => setIsSizeGuideOpen(false)}>
          <div className="pd-size-guide-modal" onClick={(e) => e.stopPropagation()}>
            <div className="pd-size-guide-head">
              <h3>Size Guide (Inches)</h3>
              <button
                type="button"
                className="pd-size-guide-close"
                onClick={() => setIsSizeGuideOpen(false)}
                aria-label="Close size guide"
              >
                ×
              </button>
            </div>
            <p className="pd-size-guide-sub">Standard regular fit garment measurements for Kurtas & Luxe sets</p>
            <div className="pd-size-table-wrap">
              <table className="pd-size-table">
                <thead>
                  <tr>
                    <th>Size</th>
                    <th>Chest</th>
                    <th>Waist</th>
                    <th>Hip</th>
                    <th>Length</th>
                  </tr>
                </thead>
                <tbody>
                  <tr><td><strong>S</strong></td><td>40"</td><td>34"</td><td>40"</td><td>42"</td></tr>
                  <tr><td><strong>M</strong></td><td>42"</td><td>36"</td><td>42"</td><td>43"</td></tr>
                  <tr><td><strong>L</strong></td><td>44"</td><td>38"</td><td>44"</td><td>44"</td></tr>
                  <tr><td><strong>XL</strong></td><td>46"</td><td>40"</td><td>46"</td><td>44"</td></tr>
                  <tr><td><strong>XXL</strong></td><td>48"</td><td>42"</td><td>48"</td><td>45"</td></tr>
                  <tr><td><strong>3XL</strong></td><td>50"</td><td>44"</td><td>50"</td><td>45"</td></tr>
                  <tr><td><strong>4XL</strong></td><td>52"</td><td>46"</td><td>52"</td><td>46"</td></tr>
                  <tr><td><strong>5XL</strong></td><td>54"</td><td>48"</td><td>54"</td><td>46"</td></tr>
                  <tr><td><strong>6XL</strong></td><td>56"</td><td>50"</td><td>56"</td><td>46"</td></tr>
                </tbody>
              </table>
            </div>
            <div className="pd-size-guide-footer">
              <span> Tip: If you are between sizes or prefer a festive relaxed look, we recommend sizing up.</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AminiiProductDetails;
